import {
  addDays,
  compareAsc,
  differenceInDays,
  differenceInMinutes,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parseISO,
  subDays,
} from "date-fns";

import { getEndOfWeek, getStartOfWeek } from "./dateFormatters";

let dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"];

export function getWeekBookends(baseDate) {
  const startDate = getStartOfWeek(baseDate);
  const endDate = getEndOfWeek(baseDate);

  return [startDate, endDate];
}

export function setupCalendarVariables(baseDate, baseDate2 = null) {
  // Set up calendar variables.
  let days = [];
  let startDate = null;
  let endDate = null;
  // Two scenarios:
  //   - only 1 date provided, setup calendar for current week around base date
  //   - two dates provided, setup calendar between the two dates

  if (baseDate && baseDate2) {
    // return;
    startDate = baseDate;
    endDate = baseDate2;

    // Setup calendar variables between the two dates.
    let firstDateBeforeSecond = isBefore(baseDate, baseDate2);
    if (firstDateBeforeSecond) {
      // setup days array between two dates

      const result = differenceInDays(baseDate, baseDate2);
      for (let i = 0; i < Math.abs(result); i++) {
        let newDate = addDays(baseDate, i);
        days.push({
          label: dayLabels[newDate.getDay()],
          date: newDate,
          data: [],
        });
      }
    }
  } else if (baseDate) {
    // Setup calendar variables around the base date.

    // let newNew = parse(baseDate);
    // console.log("new new", newNew);

    // Determine the start and end of week
    startDate = getStartOfWeek(baseDate);
    endDate = getEndOfWeek(baseDate);

    // Setup current week days
    days = [
      { label: "Mon", date: startDate, data: [] },
      { label: "Tue", date: addDays(startDate, 1), data: [] },
      { label: "Wed", date: addDays(startDate, 2), data: [] },
      { label: "Thur", date: addDays(startDate, 3), data: [] },
      { label: "Fri", date: addDays(startDate, 4), data: [] },
      { label: "Sat", date: addDays(startDate, 5), data: [] },
      { label: "Sun", date: addDays(startDate, 6), data: [] },
    ];
  }

  return { startDate, endDate, days };
}

function scheduleFilter(keepOrFilter) {
  // Takes in primaries and either filters out or keeps a specific set of primaries which
  // are required specifically for the scheduler / map functionality to work.
  // keepOrFilter = false : discard primaries matching condition
  // keepOrFilter = true : only return primaries matching condition

  let today = subDays(new Date(), 1);

  return (primary) => {
    let { status, bookedInDateTime } = primary;
    if (
      status === "arrange-date" ||
      status === "provisional" ||
      status === "in-progess" ||
      (status === "booked-in" && isAfter(parseISO(bookedInDateTime), today))
    ) {
      return keepOrFilter;
    } else {
      return !keepOrFilter;
    }
  };
}

function deduplicate(arr1, arr2) {
  return arr1.filter((x) => {
    for (let y of arr2) {
      if (y.uid === x.uid) {
        return false;
      }
    }

    return true;
  });
}

export function filterSchedulerPrimaries(
  primaries = [],
  selectedPrimaries = []
) {
  // Returns primaries which have been selected by the user and those which match
  // the scheduler condition. This condition selects for primaries which are relevant
  // to a user who needs to schedule in jobs based off of map data. For full condition
  // see schedule filter function.

  // Remove any marked primaries which match the scheduler condition, as these
  // primaries will be included in the array returned later on. (pre-emptive
  // removal of dupicates)
  let deduplicated = selectedPrimaries.filter(scheduleFilter(false));

  // Need to give each selected primary their own custom status (helps with filter in the Map)
  let selectedWithNewStatus = deduplicated.map((primary) => {
    return { ...primary, status: "selected" };
  });

  let allPrimaries = primaries.flat();

  // From allPrimaries find those which match the scheduler condition.
  let filteredPrimaries = allPrimaries.filter(scheduleFilter(true));

  let mergedPrimaries = filteredPrimaries.concat(selectedWithNewStatus);

  // Returns primaries which match the scheduler condition and also those which have
  // been specifically selected by the user.
  return mergedPrimaries;
}

export function getPrimariesBetween(primaries = [], isoStartDate, isoEndDate) {
  // Retrieve primary sheets which have been booked in between two dates.

  let flattenedPrimaries = primaries.flat();

  if (!isoStartDate && !isoEndDate) {
    return flattenedPrimaries;
  }

  let showStatus = ["booked-in", "provisional", "in-progress", "completed"];

  let thisWeeksPrimaries = flattenedPrimaries.filter((p) => {
    // Filter out all primaries which don't match status:
    // booked-in, provisional or completed
    if (!showStatus.includes(p?.status)) {
      return false;
    }

    if (p?.bookedInDateTime) {
      let bookedInDateTime = parseISO(p.bookedInDateTime);

      // console.log("date", isoStartDate, isoEndDate);

      let isWithin = false;

      // Filter out primaries not within start - end dates
      try {
        isWithin = isWithinInterval(bookedInDateTime, {
          start: isoStartDate,
          end: isoEndDate,
        });
      } catch (err) {
        // console.log("error with within dates: ", isoStartDate, isoEndDate);
      }

      return isWithin;
    }
  });
  return thisWeeksPrimaries;
}

export function fillUpDays(jobs, days) {
  // Inject booked in jobs into days array

  let daysArray = [...days];
  // Loop over jobs and assign each to the correct day in the daysArray
  jobs.forEach((j) => {
    days.forEach((d, i) => {
      let jobDate = parseISO(j.bookedInDateTime);

      if (isSameDay(d.date, jobDate)) {
        daysArray[i].data.push(j);
      }
    });
  });

  return daysArray;
}

// export function fillUpDays2(jobs, days) {
//   // Inject booked in jobs into days array

//   let daysArray = [...days];

//   // Loop over jobs and assign each to the correct day in the daysArray
//   jobs.forEach((j) => {
//     let dayIndex = getDay(parseISO(j.bookedInDateTime));

//     if (dayIndex && Number.isInteger(dayIndex)) {
//       let day = daysArray[dayIndex - 1];
//       day.data.push(j);
//     }
//   });

//   return daysArray;
// }

export function sortJobsDuringDay(days) {
  // Sort job into correct order according to their booked
  // in time.

  let sortedData = days.map((day) => {
    day.data.sort((a, b) => {
      return compareAsc(
        parseISO(a.bookedInDateTime),
        parseISO(b.bookedInDateTime)
      );
    });

    return day;
  });

  return sortedData;
}

export function addTimeDifferenceBetweenJobs(sortedDayData) {
  // Adds a divider between job cards which gives the time difference
  // between the start of the two jobs.

  let newDays = sortedDayData.map((day) => {
    if (day?.data?.length > 0) {
      let lastItem = day?.data?.length - 1;
      let newData = [];

      // Loop through jobs
      day.data.forEach((job, i) => {
        // if item is last item, add item to array.
        // compare item to next item
        // Get difference,
        // Add item and difference to array

        if (i === lastItem) {
          newData.push(job);
        } else {
          let nextJob = day.data[i + 1];

          let timeDifference = differenceInMinutes(
            parseISO(job.bookedInDateTime),
            parseISO(nextJob.bookedInDateTime)
          );

          if (timeDifference) {
            let timeBlockDetails = {
              timeBlock: true,
              timeDifference: Math.abs(timeDifference),
              uid: day.label + i,
            };

            newData.push(job);
            newData.push(timeBlockDetails);
          } else {
            newData.push(job);
            // newData.push(timeBlockDetails);
          }
        }
      });
      return { ...day, data: newData };
    }
    return day;
  });

  return newDays;
}
